import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, User, Heart, LogOut, MapPin, Trash2, Plus, CreditCard } from "lucide-react";
import { api, formatINR, formatApiError, useSEO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const field = "w-full bg-[#F5F5F5] rounded-2xl px-5 py-3.5 text-sm outline-none focus:ring-1 ring-black transition";

export default function Account() {
  useSEO("My Account");
  const { user, logout, updateProfile } = useAuth();
  const { items: wishlist, remove } = useWishlist();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showAddr, setShowAddr] = useState(false);
  const emptyAddr = { label: "Home", full_name: "", phone: "", line: "", city: "", state: "", pincode: "", is_default: false };
  const [addrForm, setAddrForm] = useState(emptyAddr);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/addresses").then((r) => setAddresses(r.data.addresses || [])).catch(() => {});
  }, [user]);

  const addAddress = async (e) => {
    e.preventDefault();
    if (!addrForm.full_name || !addrForm.line || !addrForm.city || !addrForm.pincode)
      return toast.error("Please fill name, address, city and pincode.");
    try {
      const { data } = await api.post("/addresses", addrForm);
      setAddresses(data.addresses);
      setShowAddr(false);
      setAddrForm(emptyAddr);
      toast.success("Address saved.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delAddress = async (id) => {
    try {
      const { data } = await api.delete(`/addresses/${id}`);
      setAddresses(data.addresses);
      toast.success("Address removed.");
    } catch { toast.error("Could not remove."); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile(profile);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="account-page" className="pt-32 pb-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">My Account</p>
            <h1 className="font-display text-4xl md:text-6xl tracking-tight">Hello, {user?.name?.split(" ")[0]}</h1>
          </div>
          <div className="flex items-center gap-5">
            {user?.role === "admin" && (
              <>
                <Link to="/admin/products" data-testid="admin-products-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] bg-black text-white rounded-full px-5 py-2.5">
                  <Package size={14} /> Products
                </Link>
                <Link to="/admin/orders" data-testid="admin-orders-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] border border-black rounded-full px-5 py-2.5">
                  <Package size={14} /> Orders
                </Link>
                <Link to="/admin/payments" data-testid="admin-payments-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] border border-black rounded-full px-5 py-2.5">
                  <CreditCard size={14} /> Payments
                </Link>
              </>
            )}
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] link-underline">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="bg-[#F5F5F5] rounded-full p-1 mb-10">
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white px-5 py-2 text-xs uppercase tracking-[0.12em]"><Package size={14} className="mr-2" /> Orders</TabsTrigger>
            <TabsTrigger value="wishlist" data-testid="tab-wishlist" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white px-5 py-2 text-xs uppercase tracking-[0.12em]"><Heart size={14} className="mr-2" /> Wishlist</TabsTrigger>
            <TabsTrigger value="addresses" data-testid="tab-addresses" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white px-5 py-2 text-xs uppercase tracking-[0.12em]"><MapPin size={14} className="mr-2" /> Addresses</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white px-5 py-2 text-xs uppercase tracking-[0.12em]"><User size={14} className="mr-2" /> Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" data-testid="orders-panel">
            {orders.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground mb-4">You have no orders yet.</p>
                <Link to="/collections" className="inline-block bg-black text-white rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em]">Start shopping</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.order_number} className="border border-black/10 rounded-3xl p-6" data-testid={`order-${o.order_number}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div>
                        <p className="font-display text-lg">{o.order_number}</p>
                        <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()} · {o.gateway}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">{formatINR(o.amount)}</p>
                        <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-black text-white" : "bg-[#F5F5F5]"}`}>{o.payment_status}</span>
                      </div>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      {o.items.map((it) => (
                        <div key={it.slug} className="flex items-center gap-2 text-xs bg-[#F5F5F5] rounded-full pr-4">
                          <img src={`${it.image}?w=80&q=70`} alt="" className="w-8 h-8 rounded-full object-cover" />
                          {it.name} × {it.quantity}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="wishlist" data-testid="wishlist-panel">
            {wishlist.length === 0 ? (
              <p className="text-muted-foreground text-center py-20">Your wishlist is empty.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {wishlist.map((w) => (
                  <div key={w.slug} className="group" data-testid={`wish-${w.slug}`}>
                    <Link to={`/product/${w.slug}`} className="block aspect-[4/5] rounded-3xl overflow-hidden bg-[#F5F5F5] zoom-frame">
                      <img src={`${w.image}?w=600&q=80`} alt={w.name} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex items-center justify-between mt-3">
                      <div>
                        <p className="font-display text-lg">{w.name}</p>
                        <p className="text-sm text-muted-foreground">{formatINR(w.price)}</p>
                      </div>
                      <button onClick={() => remove(w.slug)} data-testid={`wish-remove-${w.slug}`} className="text-xs uppercase tracking-widest link-underline">Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="addresses" data-testid="addresses-panel">
            <div className="flex justify-between items-center mb-6">
              <p className="text-sm text-muted-foreground">{addresses.length} saved address{addresses.length !== 1 ? "es" : ""}</p>
              <button onClick={() => setShowAddr((s) => !s)} data-testid="add-address-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] bg-black text-white rounded-full px-5 py-2.5">
                <Plus size={14} /> Add address
              </button>
            </div>
            {showAddr && (
              <form onSubmit={addAddress} className="bg-[#F5F5F5] rounded-3xl p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="address-form">
                <input data-testid="addr-name" placeholder="Full name *" value={addrForm.full_name} onChange={(e) => setAddrForm({ ...addrForm, full_name: e.target.value })} className={field} />
                <input data-testid="addr-phone" placeholder="Phone" value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} className={field} />
                <input data-testid="addr-line" placeholder="Address line *" value={addrForm.line} onChange={(e) => setAddrForm({ ...addrForm, line: e.target.value })} className={`${field} md:col-span-2`} />
                <input data-testid="addr-city" placeholder="City *" value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} className={field} />
                <input data-testid="addr-state" placeholder="State" value={addrForm.state} onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} className={field} />
                <input data-testid="addr-pincode" placeholder="Pincode *" value={addrForm.pincode} onChange={(e) => setAddrForm({ ...addrForm, pincode: e.target.value })} className={field} />
                <button type="submit" data-testid="addr-save" className="bg-black text-white rounded-full py-3.5 uppercase text-xs tracking-[0.2em]">Save address</button>
              </form>
            )}
            {addresses.length === 0 && !showAddr ? (
              <p className="text-muted-foreground text-center py-16">No saved addresses yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((a) => (
                  <div key={a.id} className="border border-black/10 rounded-3xl p-6" data-testid={`address-${a.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs uppercase tracking-[0.15em] bg-[#F5F5F5] rounded-full px-3 py-1">{a.label}{a.is_default ? " · Default" : ""}</span>
                      <button onClick={() => delAddress(a.id)} data-testid={`addr-del-${a.id}`} className="text-muted-foreground hover:text-black"><Trash2 size={16} /></button>
                    </div>
                    <p className="font-display text-lg">{a.full_name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{a.line}, {a.city}{a.state ? `, ${a.state}` : ""} — {a.pincode}</p>
                    {a.phone && <p className="text-sm text-muted-foreground">{a.phone}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" data-testid="profile-panel">
            <form onSubmit={saveProfile} className="max-w-lg space-y-4" data-testid="profile-form">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</label>
                <input disabled value={user?.email || ""} className={`${field} opacity-60 mt-2`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</label>
                <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={`${field} mt-2`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</label>
                <input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={`${field} mt-2`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</label>
                <textarea data-testid="profile-address" rows={3} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} className={`${field} mt-2 resize-none`} />
              </div>
              <button type="submit" disabled={busy} data-testid="profile-save" className="bg-black text-white rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em] disabled:opacity-60">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
